/**
 * OctileGrid.ts
 * Create, manipulate, and serialize the octile dungeon grid.
 *
 * The grid is a 19×19 interleaved array (for 10×10 octagons).
 * Operations: create, activate/deactivate cells, toggle walls, serialize.
 */

import type {
    GridCell, OctileCell, SquareConnector,
    OctileDungeon, OctileLayer, WallState, CellType, CellTheme, GridPos
} from '../types/OctileTypes.ts'
import {
    createDefaultOctileCell, createDefaultConnector,
    GRID_DEFAULTS, WALL_STATE_CYCLE
} from '../types/OctileTypes.ts'
import { getCellType, getGridDimensions, gridKey } from './GridAddressing.ts'
import { createDefaultDNA } from '../types/GrowthTypes.ts'

// ─── Grid Creation ──────────────────────────────────────────

/**
 * Create an empty interleaved grid.
 */
export function createGrid(octCols: number, octRows: number): GridCell[][] {
    const { width, height } = getGridDimensions(octCols, octRows)
    const grid: GridCell[][] = []

    for (let row = 0; row < height; row++) {
        grid[row] = []
        for (let col = 0; col < width; col++) {
            const kind = getCellType(row, col)
            if (kind === 'octagon') {
                grid[row][col] = createDefaultOctileCell(row, col)
            } else if (kind === 'connector') {
                grid[row][col] = createDefaultConnector(row, col)
            } else {
                grid[row][col] = null // void
            }
        }
    }

    return grid
}

/**
 * Create a complete dungeon with one layer.
 */
export function createDungeon(
    name: string = 'Untitled Dungeon',
    octCols: number = GRID_DEFAULTS.octagonCols,
    octRows: number = GRID_DEFAULTS.octagonRows,
    seed: number = Date.now()
): OctileDungeon {
    const { width, height } = getGridDimensions(octCols, octRows)

    const layer: OctileLayer = {
        depth: 0,
        name: 'Level 1',
        theme: 'natural_cave',
        grid: createGrid(octCols, octRows),
        ambientLight: '#1a1520',
        fogDensity: 0.02,
        ambientAudio: 'cave_drip',
    }

    return {
        id: `dungeon_${seed}`,
        name,
        seed,
        octagonCols: octCols,
        octagonRows: octRows,
        gridWidth: width,
        gridHeight: height,
        baseSquareSize: GRID_DEFAULTS.baseSquareSize,
        cornerCutRatio: GRID_DEFAULTS.cornerCutRatio,
        corridorScale: GRID_DEFAULTS.corridorScale,
        layers: [layer],
        entrance: { row: 0, col: 0, layer: 0 },
        dna: createDefaultDNA('expansive'),
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
        version: '1.0.0',
    }
}

// ─── Cell Operations ────────────────────────────────────────

/**
 * Get a cell from the grid. Returns null for void positions or out of bounds.
 */
export function getCell(grid: GridCell[][], row: number, col: number): GridCell {
    if (row < 0 || row >= grid.length) return null
    if (col < 0 || col >= grid[0].length) return null
    return grid[row][col]
}

/**
 * Activate a cell (set active = true, assign default type if empty).
 */
export function activateCell(
    grid: GridCell[][],
    row: number, col: number,
    type: CellType = 'cave',
    theme: CellTheme = 'natural_cave'
): boolean {
    const cell = getCell(grid, row, col)
    if (!cell) return false

    cell.active = true
    if (cell.kind === 'octagon') {
        cell.type = type
        cell.theme = theme
    }
    return true
}

/**
 * Deactivate a cell (set active = false, reset to defaults).
 */
export function deactivateCell(grid: GridCell[][], row: number, col: number): boolean {
    const cell = getCell(grid, row, col)
    if (!cell) return false

    cell.active = false
    if (cell.kind === 'octagon') {
        cell.type = 'empty'
        cell.walls = ['SOLID', 'SOLID', 'SOLID', 'SOLID', 'SOLID', 'SOLID', 'SOLID', 'SOLID']
        cell.spawns = []
        cell.traps = []
        cell.loot = []
        cell.partOfRoom = null
    } else {
        cell.walls = ['SOLID', 'SOLID', 'SOLID', 'SOLID']
        cell.traps = []
        cell.isPartOfRoom = null
    }
    return true
}

// ─── Wall Operations ────────────────────────────────────────

/**
 * Set a specific wall state on a cell.
 * wallIndex: 0-7 for octagons, 0-3 for connectors.
 */
export function setWall(
    grid: GridCell[][],
    row: number, col: number,
    wallIndex: number,
    state: WallState
): boolean {
    const cell = getCell(grid, row, col)
    if (!cell) return false
    if (wallIndex < 0 || wallIndex >= cell.walls.length) return false

    cell.walls[wallIndex] = state
    return true
}

/**
 * Cycle a wall to the next state in the WALL_STATE_CYCLE.
 */
export function cycleWall(
    grid: GridCell[][],
    row: number, col: number,
    wallIndex: number
): WallState | null {
    const cell = getCell(grid, row, col)
    if (!cell) return null
    if (wallIndex < 0 || wallIndex >= cell.walls.length) return null

    const currentState = cell.walls[wallIndex]
    const currentIndex = WALL_STATE_CYCLE.indexOf(currentState)
    const nextIndex = (currentIndex + 1) % WALL_STATE_CYCLE.length
    const nextState = WALL_STATE_CYCLE[nextIndex]

    cell.walls[wallIndex] = nextState
    return nextState
}

// ─── Serialization ──────────────────────────────────────────

/**
 * Serialize the grid to a sparse JSON-friendly format.
 * Only active cells are included.
 */
export function serializeGrid(grid: GridCell[][]): object[] {
    const cells: object[] = []

    for (let row = 0; row < grid.length; row++) {
        for (let col = 0; col < grid[row].length; col++) {
            const cell = grid[row][col]
            if (cell && cell.active) {
                cells.push({ r: row, c: col, ...cell })
            }
        }
    }

    return cells
}

/**
 * Deserialize sparse cell data back into a grid.
 */
export function deserializeGrid(
    data: Array<{ r: number; c: number; kind: string; [key: string]: unknown }>,
    octCols: number,
    octRows: number
): GridCell[][] {
    const grid = createGrid(octCols, octRows)

    for (const entry of data) {
        const { r, c, ...rest } = entry
        const cell = getCell(grid, r, c)
        if (cell) {
            Object.assign(cell, rest)
        }
    }

    return grid
}

/**
 * Serialize a complete dungeon to JSON.
 */
export function serializeDungeon(dungeon: OctileDungeon): string {
    const serialized = {
        ...dungeon,
        layers: dungeon.layers.map(layer => ({
            ...layer,
            grid: serializeGrid(layer.grid),
        })),
    }
    return JSON.stringify(serialized, null, 2)
}

/**
 * Count active cells of each kind in the grid.
 */
export function countActiveCells(grid: GridCell[][]): { octagons: number; connectors: number } {
    let octagons = 0
    let connectors = 0

    for (const row of grid) {
        for (const cell of row) {
            if (cell?.active) {
                if (cell.kind === 'octagon') octagons++
                else connectors++
            }
        }
    }

    return { octagons, connectors }
}

/**
 * Get all active octagonal cells.
 */
export function getActiveOctagons(grid: GridCell[][]): OctileCell[] {
    const result: OctileCell[] = []
    for (const row of grid) {
        for (const cell of row) {
            if (cell?.active && cell.kind === 'octagon') {
                result.push(cell)
            }
        }
    }
    return result
}
